import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Cpu, Database, MessageSquare, Plug, Sparkles, Wrench, Activity, AlertTriangle, X } from 'lucide-react';
import { apiFetch } from '../services/apiClient';

interface TokenBreakdown {
  systemPrompt: number;
  tools: number;
  messages: number;
  mcp: number;
  skills: number;
  other: number;
}

interface TokenUsageData {
  total: number;
  maxContext: number;
  percentage: number;
  breakdown: TokenBreakdown;
  recentCount: number;
}

interface TokenUsagePanelProps {
  compact?: boolean;
}

function formatTokens(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

function colorForPercent(pct: number): string {
  if (pct >= 80) return 'text-celestial-mars';
  if (pct >= 50) return 'text-amber-400';
  return 'text-celestial-saturn';
}

const CATEGORIES: { key: keyof TokenBreakdown; label: string; icon: React.ReactNode; color: string }[] = [
  { key: 'systemPrompt', label: '系统提示词', icon: <Cpu size={12} />, color: 'bg-violet-400' },
  { key: 'tools', label: '工具及子智能体', icon: <Wrench size={12} />, color: 'bg-cyan-400' },
  { key: 'messages', label: '对话消息', icon: <MessageSquare size={12} />, color: 'bg-celestial-saturn' },
  { key: 'mcp', label: '连接器及 MCP', icon: <Plug size={12} />, color: 'bg-emerald-400' },
  { key: 'skills', label: '技能', icon: <Sparkles size={12} />, color: 'bg-amber-400' },
  { key: 'other', label: '其他', icon: <Activity size={12} />, color: 'bg-white/30' },
];

export function TokenUsagePanel({ compact = false }: TokenUsagePanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<TokenUsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // v3.6.68: 按钮 ref + 按钮屏幕坐标
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [buttonRect, setButtonRect] = useState<{ top: number; right: number } | null>(null);
  const updateButtonRect = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonRect({ top: rect.top, right: rect.right });
    }
  };

  const fetchData = async () => {
    try {
      const res = await apiFetch('/api/token-breakdown');
      const json = await res.json();
      if (json && typeof json.total === 'number') {
        setData(json as TokenUsageData);
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, []);

  // v3.6.68: panel 是 portal,需用 buttonRef + panelRef 一起判断
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const inPanel = panelRef.current?.contains(target) || containerRef.current?.contains(target);
      const inButton = buttonRef.current?.contains(target);
      if (!inPanel && !inButton) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (loading && !data) return null;

  const total = data?.total || 0;
  const percentage = data?.percentage || 0;
  const breakdown = data?.breakdown || { systemPrompt: 0, tools: 0, messages: 0, mcp: 0, skills: 0, other: 0 };

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => { if (!isOpen) { setIsOpen(true); fetchData(); updateButtonRect(); } else { setIsOpen(false); } }}
        className={`flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1.5 text-xs font-bold transition-all hover:border-white/20 hover:bg-white/[0.08] ${
          compact ? 'px-2 py-1' : ''
        }`}
      >
        <Database size={12} className={colorForPercent(percentage)} />
        <span className={`font-mono ${colorForPercent(percentage)}`}>
          {percentage.toFixed(1)}%
        </span>
        <span className="text-white/30 font-normal">{formatTokens(total)}</span>
      </button>

      {/* v3.6.68: 用 React Portal 渲染到 body；v3.13.5: 改为向上弹出（底边锚定按钮顶部上方），避免底部工具栏遮挡 */}
      {isOpen && buttonRect && createPortal(
        <AnimatePresence>
          <motion.div
            ref={panelRef}
            initial={{ opacity: 0, x: -8, scale: 0.96 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            style={{
              position: 'fixed',
              bottom: window.innerHeight - buttonRect.top + 8,
              left: buttonRect.right + 8,
              zIndex: 100,
            }}
            className="w-72 max-h-[70vh] overflow-y-auto custom-scrollbar rounded-xl border border-white/10 bg-zinc-900/95 p-4 shadow-2xl backdrop-blur-xl"
          >
            {/* Header: 标题 + X 关闭按钮 */}
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-black uppercase tracking-widest text-white/60">
                上下文用量
              </h4>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                // v3.6.69: 加深颜色 (text-white/30 太淡) + 加 border 边框让 X 更醒目
                className="rounded-md border border-white/20 bg-white/5 p-1 text-white/70 hover:bg-red-500/20 hover:border-red-500/40 hover:text-red-400 transition-colors"
                title="关闭 (Esc)"
              >
                <X size={16} />
              </button>
            </div>

            {/* v3.6.68: 大百分比 + 数值 (参考 WB) */}
            <div className="mb-3 flex items-baseline gap-3">
              <span className={`text-3xl font-black font-mono ${colorForPercent(percentage)}`}>
                {percentage.toFixed(1)}%
              </span>
              <div className="flex flex-col text-[10px] text-white/40">
                <span>已使用 <span className="font-mono text-white/70 font-bold">{formatTokens(total)}</span></span>
                <span>总量 <span className="font-mono text-white/55 font-bold">{formatTokens(data?.maxContext || 262144)}</span></span>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mb-3">
              <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(percentage, 100)}%` }}
                  className={`h-full rounded-full ${
                    percentage >= 90 ? 'bg-celestial-mars' : percentage >= 70 ? 'bg-amber-400' : 'bg-celestial-saturn'
                  }`}
                  transition={{ duration: 0.5 }}
                />
              </div>
              <div className="flex justify-between mt-1 text-[9px] text-white/20">
                <span>安全</span>
                <span className="text-amber-400/50">70%</span>
                <span className="text-celestial-mars/50">90%</span>
              </div>
            </div>

            {/* Warning */}
            {percentage >= 90 && (
              <div className="mb-3 flex items-start gap-2 rounded-lg bg-celestial-mars/10 border border-celestial-mars/20 p-2.5 text-[11px] text-celestial-mars">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold">上下文接近满载</p>
                  <p className="text-white/50 mt-0.5">新对话可能被截断，建议开启新会话或手动清理历史。</p>
                </div>
              </div>
            )}
            {percentage >= 70 && percentage < 90 && (
              <div className="mb-3 flex items-start gap-2 rounded-lg bg-amber-400/10 border border-amber-400/20 p-2.5 text-[11px] text-amber-400">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <div className="text-white/60">上下文用量较高，后续消息可能触发截断。</div>
              </div>
            )}

            {/* v3.6.69: 所有分类都显示 (0 值用更淡的样式),让 5 项完整呈现 */}
            <div className="space-y-1.5">
              {CATEGORIES.map((cat) => {
                const val = breakdown[cat.key] || 0;
                const isZero = val === 0;
                return (
                  <div key={cat.key} className={`flex items-center gap-2 ${isZero ? 'opacity-40' : ''}`}>
                    <div className={`h-2.5 w-2.5 rounded-full ${cat.color} shrink-0`} />
                    <span className="flex-1 text-xs font-medium text-white/70">{cat.label}</span>
                    <span className="text-xs font-mono text-white/45">
                      {isZero ? '0' : '~' + formatTokens(val)}
                    </span>
                  </div>
                );
              })}
            </div>

            {data && data.recentCount > 0 && (
              <div className="mt-3 pt-2 border-t border-white/5 text-[10px] text-white/30">
                最近一次调用上下文 · 共 {data.recentCount} 次调用
              </div>
            )}
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
